import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ErpModal,
  ErpFormGroup,
  ErpFormInput,
  ErpRow,
  ErpCol,
  ErpButton,
  ErpFormCheckbox,
  ErpDiv,
  ErpLabelRaw,
  ErpInputRaw,
  ErpH5,
  ErpSpan,
  ErpH4,
  ErpButtonRaw,
} from '@/components/erp';
import { useTheme } from '@/components/providers/ThemeProvider';
import { useSwal } from '@/hooks/useSwal';
import { generateThemeVariables } from '@/utils/colorUtils';

export interface ThemeBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThemeBuilderModal: React.FC<ThemeBuilderModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { addCustomTheme, setTheme } = useTheme();
  const { toastError } = useSwal();

  const [themeName, setThemeName] = useState('My Custom Theme');
  const [primaryColor, setPrimaryColor] = useState('#8B5CF6'); // Default to a nice purple
  
  const [useSecondary, setUseSecondary] = useState(false);
  const [secondaryColor, setSecondaryColor] = useState('#3b82f6'); // Default blue
  
  const [useBackground, setUseBackground] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#0f172a'); // Default slate
  
  const [useTextColor, setUseTextColor] = useState(false);
  const [textColor, setTextColor] = useState('#333333');
  
  const [useBorderColor, setUseBorderColor] = useState(false);
  const [borderColor, setBorderColor] = useState('#e2e8f0');

  const [radius, setRadius] = useState(0.5); // Default to standard radius
  
  const [useBorderWidth, setUseBorderWidth] = useState(false);
  const [borderWidth, setBorderWidth] = useState(1); // Default to 1px

  const [previewMode, setPreviewMode] = useState<'light' | 'dark'>('light');

  const handleSave = () => {
    if (!themeName.trim()) return;

    // crypto.randomUUID is collision-resistant; Math.random().toString(36) is not.
    const newThemeId = `custom-${
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 10)}`
    }`;

    // addCustomTheme/setTheme persist to localStorage synchronously, which
    // throws in private mode / on quota exhaustion. Keep the modal open and
    // tell the user instead of failing silently.
    try {
      addCustomTheme({
        id: newThemeId,
        name: themeName,
        primaryColor,
        secondaryColor: useSecondary ? secondaryColor : undefined,
        backgroundColor: useBackground ? backgroundColor : undefined,
        textColor: useTextColor ? textColor : undefined,
        borderColor: useBorderColor ? borderColor : undefined,
        borderWidth: useBorderWidth ? borderWidth : undefined,
        radius
      });

      setTheme(newThemeId);
      onClose();
    } catch (error) {
      console.error('Failed to persist custom theme', error);
      toastError(t('Failed_to_save_theme'));
    }
  };

  // Generate preview variables
  const previewVariables = generateThemeVariables(
    primaryColor, 
    radius, 
    previewMode === 'dark',
    useSecondary ? secondaryColor : undefined,
    useBackground ? backgroundColor : undefined,
    useTextColor ? textColor : undefined,
    useBorderColor ? borderColor : undefined,
    useBorderWidth ? borderWidth : undefined
  );

  const previewStyle = Object.entries(previewVariables).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as any] = value;
    }
    return acc;
  }, {} as any);

  // Helper to render a color field
  const renderColorField = (
    label: string, 
    useFlag: boolean, 
    setUseFlag: (v: boolean) => void, 
    colorValue: string, 
    setColorValue: (v: string) => void,
    description: string
  ) => (
    <ErpFormGroup>
      <ErpDiv className="flex items-center justify-between mb-2">
        <ErpLabelRaw className="text-sm font-medium">{label}</ErpLabelRaw>
        <ErpFormCheckbox 
          label={t('Custom')}
          checked={useFlag}
          onChange={(checked) => setUseFlag(checked as boolean)}
        />
      </ErpDiv>
      <ErpDiv className={`flex items-center gap-3 transition-opacity ${!useFlag ? 'opacity-50 pointer-events-none' : ''}`}>
        <ErpInputRaw 
          type="color" 
          value={colorValue} 
          onChange={(e) => setColorValue(e.target.value)}
          className="w-12 h-12 p-1 rounded cursor-pointer border bg-white"
        />
        <ErpFormInput 
          value={colorValue} 
          onChange={(value: string) => {
            // Accept an optional leading '#' plus up to 6 hex digits only, so
            // generateThemeVariables can never receive a malformed colour.
            const next = value;
            if (/^#?[0-9a-fA-F]{0,6}$/.test(next)) setColorValue(next);
          }} 
          className="flex-1 uppercase font-mono text-sm"
          maxLength={7}
        />
      </ErpDiv>
      {!useFlag && <ErpDiv className="text-xs text-muted-foreground mt-1">{t(description)}</ErpDiv>}
    </ErpFormGroup>
  );

  return (
    <ErpModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('Create Custom Theme')}
      size="xl"
      footer={
        <ErpDiv className="flex justify-end gap-2">
          <ErpButton variant="outline-secondary" onClick={onClose}>{t('Cancel')}</ErpButton>
          <ErpButton variant="primary" onClick={handleSave} disabled={!themeName.trim()}>
            {t('Save & Apply')}
          </ErpButton>
        </ErpDiv>
      }
    >
      <ErpRow>
        <ErpCol md={6} className="border-r pr-4 max-h-[70vh] overflow-y-auto scrollbar-thin">
          <ErpDiv className="flex flex-col gap-6 pb-4">
            
            <ErpDiv className="space-y-4">
              <ErpH5 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2">{t('Basic Settings')}</ErpH5>
              <ErpFormGroup label={t('Theme Name')} required>
                <ErpFormInput 
                  value={themeName} 
                  onChange={(value: string) => setThemeName(value)}
                  placeholder={t('e.g. Ocean Blue')} 
                />
              </ErpFormGroup>

              <ErpFormGroup label={t('Primary Color (Base Theme Color)')}>
                <ErpDiv className="flex items-center gap-3">
                  <ErpInputRaw 
                    type="color" 
                    value={primaryColor} 
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-12 p-1 rounded cursor-pointer border bg-white"
                  />
                  <ErpFormInput 
                    value={primaryColor} 
                    onChange={(value: string) => setPrimaryColor(value)}
                    className="flex-1 uppercase font-mono text-sm"
                    maxLength={7}
                  />
                </ErpDiv>
              </ErpFormGroup>
            </ErpDiv>

            <ErpDiv className="space-y-4">
              <ErpH5 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2">{t('Advanced Colors')}</ErpH5>
              {renderColorField(t('Secondary Color'), useSecondary, setUseSecondary, secondaryColor, setSecondaryColor, 'Derived from primary hue')}
              {renderColorField(t('Base Background Hue'), useBackground, setUseBackground, backgroundColor, setBackgroundColor, 'Sets light/dark background tint')}
              {renderColorField(t('Text Color (Foreground)'), useTextColor, setUseTextColor, textColor, setTextColor, 'Automatically contrasted against background')}
              {renderColorField(t('Border & Input Color'), useBorderColor, setUseBorderColor, borderColor, setBorderColor, 'Derived from background tint')}
            </ErpDiv>

            <ErpDiv className="space-y-4">
              <ErpH5 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b pb-2">{t('Shapes & Borders')}</ErpH5>
              <ErpFormGroup label={t('Border Radius')}>
                <ErpDiv className="flex items-center gap-2">
                  <ErpInputRaw 
                    type="range" 
                    min="0" 
                    max="2" 
                    step="0.125"
                    value={radius}
                    onChange={(e) => setRadius(parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <ErpSpan className="text-sm font-mono w-12 text-right">{radius}rem</ErpSpan>
                </ErpDiv>
                <ErpDiv className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
                  <ErpSpan>Sharp</ErpSpan>
                  <ErpSpan>Rounded</ErpSpan>
                  <ErpSpan>Pill</ErpSpan>
                </ErpDiv>
              </ErpFormGroup>
              
              <ErpFormGroup>
                <ErpDiv className="flex items-center justify-between mb-2">
                  <ErpLabelRaw className="text-sm font-medium">{t('Border Width')}</ErpLabelRaw>
                  <ErpFormCheckbox 
                    label={t('Custom')}
                    checked={useBorderWidth}
                    onChange={(checked) => setUseBorderWidth(checked as boolean)}
                  />
                </ErpDiv>
                <ErpDiv className={`flex items-center gap-2 transition-opacity ${!useBorderWidth ? 'opacity-50 pointer-events-none' : ''}`}>
                  <ErpInputRaw 
                    type="range" 
                    min="0" 
                    max="4" 
                    step="1"
                    value={borderWidth}
                    onChange={(e) => setBorderWidth(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <ErpSpan className="text-sm font-mono w-12 text-right">{borderWidth}px</ErpSpan>
                </ErpDiv>
                {!useBorderWidth && <ErpDiv className="text-xs text-muted-foreground mt-1 px-1">Default 1px standard tailwind borders</ErpDiv>}
              </ErpFormGroup>
            </ErpDiv>
            
          </ErpDiv>
        </ErpCol>
        
        <ErpCol md={6} className="pl-4 flex flex-col min-h-0">
          <ErpDiv className="mb-3 flex justify-between items-center shrink-0">
            <ErpH4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{t('Preview')}</ErpH4>
            <ErpDiv className="flex gap-1 bg-muted p-1 rounded-md">
              <ErpButtonRaw 
                className={`px-3 py-1 text-xs rounded-sm ${previewMode === 'light' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                onClick={() => setPreviewMode('light')}
              >
                {t('Light')}
              </ErpButtonRaw>
              <ErpButtonRaw 
                className={`px-3 py-1 text-xs rounded-sm ${previewMode === 'dark' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                onClick={() => setPreviewMode('dark')}
              >
                {t('Dark')}
              </ErpButtonRaw>
            </ErpDiv>
          </ErpDiv>

          <ErpDiv 
            className="w-full flex-1 border rounded-md p-6 flex flex-col gap-4 overflow-hidden relative transition-colors duration-300"
            style={{
              ...previewStyle,
              backgroundColor: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))',
              borderColor: 'hsl(var(--border))',
              borderWidth: useBorderWidth ? `${borderWidth}px` : '1px'
            }}
          >
            {/* Mock Header */}
            <ErpDiv className="flex justify-between items-center pb-4 border-b" style={{ borderColor: 'hsl(var(--border))', borderWidth: useBorderWidth ? `0 0 ${borderWidth}px 0` : '0 0 1px 0' }}>
              <ErpDiv className="font-bold text-lg">{themeName || 'Preview'}</ErpDiv>
              <ErpDiv 
                className="w-8 h-8 flex items-center justify-center text-xs font-bold"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                  borderRadius: 'hsl(var(--radius))'
                }}
              >
                JD
              </ErpDiv>
            </ErpDiv>

            {/* Mock Content */}
            <ErpDiv className="flex gap-4 flex-1">
              {/* Mock Sidebar */}
              <ErpDiv className="w-1/3 flex flex-col gap-3 pt-2">
                <ErpDiv className="h-2.5 w-3/4 rounded-full" style={{ backgroundColor: 'hsl(var(--muted))' }}></ErpDiv>
                <ErpDiv className="h-2.5 w-full rounded-full" style={{ backgroundColor: 'hsl(var(--muted))' }}></ErpDiv>
                <ErpDiv className="h-2.5 w-5/6 rounded-full" style={{ backgroundColor: 'hsl(var(--muted))' }}></ErpDiv>
                <ErpDiv className="mt-4 flex items-center gap-2 p-2 rounded-md" style={{ backgroundColor: 'hsl(var(--secondary))', color: 'hsl(var(--secondary-foreground))' }}>
                    <ErpDiv className="h-2 w-2 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }}></ErpDiv>
                    <ErpSpan className="text-xs font-medium">Active Item</ErpSpan>
                </ErpDiv>
              </ErpDiv>
              
              {/* Mock Main Area */}
              <ErpDiv className="w-2/3 flex flex-col gap-3">
                <ErpDiv 
                  className="p-4 shadow-sm flex flex-col gap-3"
                  style={{
                    backgroundColor: 'hsl(var(--card))',
                    color: 'hsl(var(--card-foreground))',
                    borderColor: 'hsl(var(--border))',
                    borderWidth: useBorderWidth ? `${borderWidth}px` : '1px',
                    borderStyle: 'solid',
                    borderRadius: 'hsl(var(--radius))'
                  }}
                >
                  <ErpDiv className="h-3 w-1/2 rounded-full" style={{ backgroundColor: 'hsl(var(--foreground))' }}></ErpDiv>
                  <ErpDiv className="h-2 w-full rounded-full" style={{ backgroundColor: 'hsl(var(--muted))' }}></ErpDiv>
                  <ErpDiv className="h-2 w-4/5 rounded-full" style={{ backgroundColor: 'hsl(var(--muted))' }}></ErpDiv>
                  
                  <ErpInputRaw 
                    className="mt-2 p-2 text-xs w-full outline-none focus:ring-1"
                    placeholder="Interactive Input"
                    style={{ 
                        borderColor: 'hsl(var(--border))', 
                        backgroundColor: 'hsl(var(--background))',
                        color: 'hsl(var(--foreground))',
                        borderWidth: useBorderWidth ? `${borderWidth}px` : '1px',
                        borderStyle: 'solid',
                        borderRadius: 'calc(var(--radius) - 2px)'
                    }} 
                  />
                </ErpDiv>

                <ErpDiv className="flex gap-2 mt-auto justify-end">
                  <ErpDiv 
                    className="px-3 py-1.5 text-xs font-medium cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: 'transparent',
                      color: 'hsl(var(--foreground))',
                      borderColor: 'hsl(var(--border))',
                      borderWidth: useBorderWidth ? `${borderWidth}px` : '1px',
                      borderStyle: 'solid',
                      borderRadius: 'hsl(var(--radius))'
                    }}
                  >
                    Cancel
                  </ErpDiv>
                  <ErpDiv 
                    className="px-3 py-1.5 text-xs font-medium cursor-pointer hover:opacity-90 flex items-center justify-center"
                    style={{
                      backgroundColor: 'hsl(var(--primary))',
                      color: 'hsl(var(--primary-foreground))',
                      borderWidth: useBorderWidth ? `${borderWidth}px` : '0px',
                      borderColor: 'hsl(var(--primary))',
                      borderStyle: 'solid',
                      borderRadius: 'hsl(var(--radius))'
                    }}
                  >
                    Primary Action
                  </ErpDiv>
                </ErpDiv>
              </ErpDiv>
            </ErpDiv>
          </ErpDiv>
        </ErpCol>
      </ErpRow>
    </ErpModal>
  );
};

export default ThemeBuilderModal;
